import { ArrowLeftIcon, ArrowRightIcon } from 'cokitchen-icons';
import SearchInput from 'components/base/search-input';
import BatchedOrderBrandCard from 'components/batched-order-brand-card';
import PreorderDeliveryModal from 'components/modals/preorder-delivery-modal';
import useDashboardContext from 'contexts/dashboard.context';
import useOrdersContext from 'contexts/orders.context';
import { BrandType } from 'models/brand.model';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Bars } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';
import { apiService } from 'services/base.service';
import { ROUTE_KEYS } from 'utils/constants';
import { ENUM_BRAND_TYPE } from 'utils/enum';
import { getTimeRange, handleApiError, hasTimePassed } from 'utils/functions';

const BatchedOrdersBrands = () => {
  const { activeCompany, setBackLink, setBackLinkTitle, setIsBackLink, setIsBasketDisabled } =
    useDashboardContext();
  const openToday = activeCompany.operational_days[moment().day()]?.is_active || false;
  const openTomorrow =
    activeCompany.operational_days[moment().add(1, 'day').day()]?.is_active || false;
  const daysToShow = [
    ...(openToday ? ['Today'] : []),
    'Pre-order Only',
    ...(openTomorrow ? ['Tomorrow'] : [])
  ];
  const [value, setValue] = useState('');
  const [brands, setBrands] = useState<BrandType[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<BrandType[]>([]);
  const [loading, setLoading] = useState(false);
  const [day, setDay] = useState(daysToShow[0]);

  const nextDay = daysToShow[daysToShow.findIndex((d) => d === day) + 1];
  const previousDay = daysToShow[daysToShow.findIndex((d) => d === day) - 1];

  const { setDeliveryDate, calculatedOrderForm, setcalculatedOrderForm } = useOrdersContext();
  const [brand, setBrand] = useState<BrandType | null>(null);
  const navigate = useNavigate();

  const fetchFcbBrands = async () => {
    try {
      setLoading(true);
      if (!activeCompany?.id) return;
      const res = await apiService.get<{ data: BrandType[] }>(`brand/${activeCompany.id}/company`, {
        baseURL: process.env.REACT_APP_BRAND_API_URL,
        params: { company_id: activeCompany.id }
      });
      const brandsWithDeliveryTime = await Promise.all(
        res.data.data.map(async (b) => {
          try {
            const res = await apiService.get(`company/${activeCompany.id}/delivery-time`, {
              params: { brand_id: b.id }
            });
            return { ...b, delivery_time: getTimeRange(res.data.data.delivery_time) };
          } catch (error) {
            return { ...b, delivery_time: b.preorder ? '' : null };
          }
        })
      );
      setBrands(brandsWithDeliveryTime.filter((b) => b.live && b.delivery_time !== null));
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (brand: BrandType) => {
    if (brand.brand_type === ENUM_BRAND_TYPE.FOODCOURT_BUSINESS) return setBrand(brand);
    navigate(`/orders/restaurants/${brand.id}?type=batch`);
  };

  const setDeliveryTime = ({
    preorder_delivery_date,
    preorder_delivery_time
  }: {
    preorder_delivery_date: string;
    preorder_delivery_time: string;
  }) => {
    setcalculatedOrderForm({
      ...calculatedOrderForm,
      preorder_delivery_date,
      preorder_delivery_time
    });
    if (brand) navigate(`/orders/restaurants/${brand.id}?type=batch`);
  };

  useEffect(() => {
    fetchFcbBrands();
  }, []);

  useEffect(() => {
    setIsBackLink(true);
    setIsBasketDisabled(false);
    setBackLinkTitle('Return to Previous');
    setBackLink(`/${ROUTE_KEYS.ORDERS}`);

    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
      setBackLink('');
    };
  }, []);

  useEffect(() => {
    if (day === 'Today') setDeliveryDate(moment().format('YYYY-MM-DD'));
    if (day === 'Tomorrow') setDeliveryDate(moment().add(1, 'day').format('YYYY-MM-DD'));
  }, [day]);

  useEffect(() => {
    if (value !== '') {
      const filtered = brands.filter((r) => r.name.toLowerCase().includes(value.toLowerCase()));
      setFilteredBrands(filtered);
    } else {
      setFilteredBrands(brands);
    }
  }, [value, brands]);

  const todayBrands = useMemo(() => {
    if (!openToday) return [];
    return filteredBrands.filter(
      (brand) => !brand.preorder && !hasTimePassed(moment().format('h:mma'), brand?.cutoff_time)
    );
  }, [filteredBrands]);

  const tomorrowBrands = useMemo(() => {
    if (!openTomorrow) return [];
    return filteredBrands.filter(
      (brand) => !brand.preorder && hasTimePassed(moment().format('h:mma'), brand?.cutoff_time)
    );
  }, [filteredBrands]);

  const preorderBrands = useMemo(() => {
    return filteredBrands.filter((b) => b.preorder);
  }, [filteredBrands]);

  const brandsToshow = useMemo(() => {
    return (
      {
        Today: todayBrands,
        Tomorrow: tomorrowBrands,
        'Pre-order Only': preorderBrands
      }[day] || []
    );
  }, [filteredBrands, day]);

  return (
    <div>
      <div className='flex justify-between mt-5 gap-4 mb-10'>
        <div>
          <h1 className='text-[20px] font-bold'>FoodCourt Business Restaurants</h1>
          <p className='whitespace-nowrap text-xs mt-1 text-[#6F7376]'>
            Select your preferred restaurants
          </p>
        </div>
        <div>
          <SearchInput
            placeholder='search'
            value={value}
            handleChange={(e) => setValue(e.target.value)}
          />
        </div>
      </div>
      <div className='flex items-center gap-2'>
        <span>{day}</span>
        <div
          className={`w-[22px] h-[22px] rounded-full bg-[#F4F1F1] grid place-items-center  ${
            previousDay ? 'cursor-pointer' : 'cursor-not-allowed'
          }`}
          onClick={previousDay ? () => setDay(previousDay) : undefined}>
          <ArrowLeftIcon className={`${!previousDay && 'text-[#B6B8BA]'}`} />
        </div>
        <div
          className={`w-[22px] h-[22px] rounded-full bg-[#F4F1F1] grid place-items-center ${
            nextDay ? 'cursor-pointer' : 'cursor-not-allowed'
          }`}
          onClick={nextDay ? () => setDay(nextDay) : undefined}>
          <ArrowRightIcon className={`${!nextDay && 'text-[#B6B8BA]'}`} />
        </div>
      </div>
      {loading ? (
        <div className='grow w-full h-screen flex justify-center pt-[25vh]'>
          <Bars color='#E60A2B' height={50} width={50} />
        </div>
      ) : brandsToshow.length === 0 ? (
        <div className='w-full h-[40vh] flex justify-center items-center'>
          <p className='text-gray-9 text-center w-full'>No brands found</p>
        </div>
      ) : (
        <div className='w-full mx-auto flex flex-col gap-8 py-10'>
          <div className='w-full grid grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] gap-5'>
            {brandsToshow.map((brand) => (
              <BatchedOrderBrandCard
                key={brand.id}
                brand={brand}
                handleClick={() => handleClick(brand)}
                day={day}
              />
            ))}
          </div>
        </div>
      )}

      {brand ? (
        <PreorderDeliveryModal
          brand={brand}
          open={Boolean(brand)}
          onClose={() => setBrand(null)}
          onConfirm={setDeliveryTime}
        />
      ) : null}
    </div>
  );
};

export default BatchedOrdersBrands;
